import { Link, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { keyToDisplayName } from '../../utils/modelFamilies';

interface BreadcrumbSegment {
  label: string;
  path: string;
}

const routeLabels: Record<string, string> = {
  models: 'Models',
  providers: 'Providers',
  compliance: 'Compliance',
  compare: 'Compare',
  guides: 'Guides',
  governance: 'Governance',
  changes: 'Changes',
  changelog: 'Changelog',
  about: 'About',
  profile: 'Profile',
  search: 'Search',
  aws: 'AWS Bedrock',
  openai: 'OpenAI',
  hipaa: 'HIPAA',
  gdpr: 'GDPR',
  soc2: 'SOC 2',
  fedramp: 'FedRAMP',
  residency: 'Data Residency',
  archive: 'Archive',
  subscribe: 'Subscribe',
  'eu-ai-models': 'EU AI Models',
  'hipaa-ai-model-providers': 'HIPAA AI Model Providers',
  'aws-bedrock-region-availability': 'AWS Bedrock Region Availability',
  'bedrock-vs-anthropic': 'AWS Bedrock vs Anthropic',
  'openai-vs-azure-openai-residency': 'OpenAI vs Azure OpenAI Residency',
};

// Friendly names for model publisher slugs used in /models/:publisher URLs
const providerLabels: Record<string, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
  amazon: 'Amazon',
  google: 'Google',
  meta: 'Meta',
  cohere: 'Cohere',
  'stability ai': 'Stability AI',
  'mistral ai': 'Mistral AI',
  deepseek: 'DeepSeek',
};

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function Breadcrumbs() {
  const location = useLocation();

  const segments = useMemo((): BreadcrumbSegment[] => {
    if (location.pathname === '/') return [];

    const parts = location.pathname.split('/').filter(Boolean);
    const result: BreadcrumbSegment[] = [];

    let currentPath = '';
    for (const part of parts) {
      currentPath += `/${part}`;
      const decoded = decodeSegment(part);
      const lower = decoded.toLowerCase();

      let label: string;
      if (routeLabels[lower]) {
        // Known static route segment
        label = routeLabels[lower];
      } else {
        const prevPath = result[result.length - 1]?.path ?? '';
        if (prevPath === '/models') {
          // /models/:provider → use friendly provider name
          label = providerLabels[lower] ?? (decoded.charAt(0).toUpperCase() + decoded.slice(1));
        } else if (prevPath.startsWith('/models/')) {
          // /models/:provider/:family → convert kebab key to display name
          label = keyToDisplayName(decoded);
        } else {
          label = decoded;
        }
      }

      result.push({ label, path: currentPath });
    }

    return result;
  }, [location.pathname]);

  if (segments.length === 0) return null;

  return (
    <nav className="flex min-w-0 items-center gap-3 text-sm font-medium text-slate-700" aria-label="Breadcrumb">
      <Link to="/" className="whitespace-nowrap text-slate-600 transition-colors hover:text-slate-950">
        Dashboard
      </Link>
      {segments.map((segment, i) => (
        <span key={segment.path} className="flex min-w-0 items-center gap-3">
          <span className="text-slate-300">/</span>
          {i === segments.length - 1 ? (
            <span className="truncate font-semibold text-slate-950">{segment.label}</span>
          ) : (
            <Link to={segment.path} className="truncate text-slate-600 transition-colors hover:text-slate-950">
              {segment.label}
            </Link>
          )}
        </span>
      ))}
    </nav>
  );
}
